import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import type { AgentParameterDto, CreateAgentParameterRequest, UpdateAgentParameterRequest } from '../../types/agent';

interface ParameterFormProps {
  parameter: AgentParameterDto | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAgentParameterRequest | UpdateAgentParameterRequest) => void;
}

const PARAMETER_TYPES = ['string', 'number', 'boolean', 'json'];

/**
 * Parameter form component for creating and editing agent parameters.
 */
export const ParameterForm = ({ parameter, open, onClose, onSubmit }: ParameterFormProps) => {
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('string');
  const [defaultValue, setDefaultValue] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isSecret, setIsSecret] = useState(false);
  const [validationJson, setValidationJson] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (parameter) {
      setKey(parameter.key);
      setLabel(parameter.label);
      setDescription(parameter.description || '');
      setType(parameter.type);
      setDefaultValue(parameter.defaultValue || '');
      setIsRequired(parameter.isRequired);
      setIsSecret(parameter.isSecret);
      setValidationJson(parameter.validationJson || '');
    } else {
      setKey('');
      setLabel('');
      setDescription('');
      setType('string');
      setDefaultValue('');
      setIsRequired(false);
      setIsSecret(false);
      setValidationJson('');
    }
    setError(null);
  }, [parameter, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    if (!key.trim()) {
      setError('Key is required');
      return;
    }

    if (!label.trim()) {
      setError('Label is required');
      return;
    }

    // Validate JSON if provided
    if (validationJson) {
      try {
        JSON.parse(validationJson);
      } catch {
        setError('Validation JSON must be valid JSON');
        return;
      }
    }

    const data: CreateAgentParameterRequest | UpdateAgentParameterRequest = {
      ...(parameter ? { id: parameter.id } : {}),
      key: key.trim(),
      label: label.trim(),
      description: description.trim() || undefined,
      type,
      defaultValue: defaultValue.trim() || undefined,
      isRequired,
      isSecret,
      validationJson: validationJson.trim() || undefined,
    };

    onSubmit(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{parameter ? 'Edit Parameter' : 'Add Parameter'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              required
              fullWidth
              disabled={!!parameter}
              helperText="Unique identifier for the parameter (cannot be changed after creation)"
            />
            <TextField
              label="Label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
                {PARAMETER_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label="Default Value"
              value={defaultValue}
              onChange={(e) => setDefaultValue(e.target.value)}
              fullWidth
              type={isSecret ? 'password' : 'text'}
            />
            <FormControlLabel
              control={<Checkbox checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />}
              label="Required"
            />
            <FormControlLabel
              control={<Checkbox checked={isSecret} onChange={(e) => setIsSecret(e.target.checked)} />}
              label="Secret (mask in UI)"
            />
            <TextField
              label="Validation JSON (optional)"
              value={validationJson}
              onChange={(e) => setValidationJson(e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="JSON schema for parameter validation"
              sx={{ fontFamily: 'monospace' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {parameter ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
